import * as React from 'react'
import { useLocation } from 'react-router-dom'

import 'container-query-polyfill'
import {
  FactorialOneLayoutDocument,
  FetchSidebarFavoritesDocument,
  TrialFeaturesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import AnimatedFlash from 'modules/core/components/AnimatedFlash'
import CurrentProgress from 'modules/core/components/CurrentProgress'
import DeletionBanner from 'modules/core/components/DeletionBanner'
import { useSection } from 'modules/core/components/SectionContainer'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useGetSiteControl } from 'modules/core/hooks/useGetSiteControl'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import { useCommandBarMenuItems, useMenuTree, useSidebarModules } from 'modules/core/lib/sidebar'
import companies from 'modules/core/store/collections/companies'

import BecomeBanner from 'modules/authentication/components/BecomeBanner'
import CompanyDeletionModal from 'modules/billing/components/CompanyDeletionModal'
import TrialExpiredModal from 'modules/billing/components/TrialExpiredModal'
import UnpaidBanner from 'modules/billing/components/UnpaidBanner'
import {
  companyInExpiredFreeTrial,
  companyInFreeTrial,
  companySubscription,
} from 'modules/billing/store/collections/subscriptions'
import ProfessionalsBanner from 'modules/bookkeepers/components/ProfessionalsBanner'
import { useBookkeepersProfessionals } from 'modules/bookkeepers/hooks/useBookkeepersProfessionals'
import CommandBar from 'modules/command_bar/components/CommandBar'
import { CommandBarContextProvider } from 'modules/command_bar/lib/commandBarContext'
import useIsContractFlows from 'modules/contracts/hooks/useIsContractFlows'
import OverlimitEmployeesBanner from 'modules/employees/components/OverlimitEmployees/Banner'
import useOverlimitEmployeesChecker from 'modules/employees/components/OverlimitEmployees/useOverlimitEmployeesChecker'
import { InAppCommsProvider } from 'modules/in_app_comms/store/InAppCommsProvider'
import { BannerProvider } from 'modules/layout/hooks/useBannerContext'
import { useLayoutContext } from 'modules/layout/hooks/useFullscreen'
import NpsWidget from 'modules/nps/components/Widget'
import BookAMeetingBanner from 'modules/onboarding/components/BookAMeetingBanner'
import HelpButton from 'modules/onboarding/components/HelpButton'
import OmniButton from 'modules/onboarding/components/OmniButton'
import TrialBanner from 'modules/onboarding/components/TrialBanner'
import useIsPayrollOnboardingFlow from 'modules/payroll_bundle/hooks/useIsPayrollOnboardingFlow'

import { FactorialOneLayout } from './FactorialOneLayout'
import { NewNotifications } from './Notifications'
import { useReloadOutdatedApp } from './hooks/useReloadOutdatedApp'

const RootLayout: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { access, employee, canManageSubscription, isAdminBecome, isStaffBecome } =
    useCurrentSession()
  const isBecome = isAdminBecome || isStaffBecome

  const [fetchSidebarData, { loading: sidebarLoading, data: sidebarData }] = useLazyQuery(
    FactorialOneLayoutDocument,
    {
      variables: {
        moduleIds: useSidebarModules(),
      },
    }
  )
  const [fetchSidebarFavorites, { loading: loadingFavoritesSidebar, data: sidebarFavoritesData }] =
    useLazyQuery(FetchSidebarFavoritesDocument)
  const { selected: company } = companies

  const [
    fetchTrialFeatures,
    { loading: _loadingTrialFeatures, data: trialFeaturesData, error: _trialFeaturesError },
  ] = useLazyQuery(TrialFeaturesDocument)
  const { pathname } = useLocation()
  const { level } = useSection()

  const [isDeletion, setDeletion] = React.useState(false)
  const [isCommandBarOpen, setCommandBarOpen] = React.useState(false)

  const menuCategories = useMenuTree(
    sidebarData?.apiCore?.moduleRegistriesConnection?.nodes.map(({ id }) => id) ?? []
  )

  const commandBarMenuItems = useCommandBarMenuItems(
    sidebarData?.apiCore?.moduleRegistriesConnection?.nodes.map(({ id }) => id) ?? []
  )

  useReloadOutdatedApp()

  const { identify } = useGetSiteControl()

  useEffect(() => {
    fetchSidebarData()
    fetchSidebarFavorites()
    identify()
    deletion()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (company?.id) {
      fetchTrialFeatures({
        variables: {
          companyId: company.id,
        },
      })
    }
  }, [company?.id, fetchTrialFeatures])

  useEffect(() => {
    track('Page View', {
      page_path: pathname,
      page_level: level,
      page_url: window.location.href,
      page_title: document.title,
    })
  }, [pathname, level])

  const currentSubscription = company ? companySubscription(company) : null
  const hasRegularAccess = !employee || employee.hasRegularAccess
  const inFreeTrial = company ? companyInFreeTrial(company) : false
  const inExpiredFreeTrial = company ? companyInExpiredFreeTrial(company) : false
  const inDeletion = company?.deletion
  const dueBannerState = currentSubscription?.dueBannerState
  const { isOverlimit } = useOverlimitEmployeesChecker()
  const { isEmbedded } = useLayoutContext()
  const { loading: loadingBookkeepers, bookkeepers } = useBookkeepersProfessionals()
  const isBookkeeper = !!bookkeepers.find({
    access_id: access.id,
  })

  const deletion = () => {
    if (inDeletion && new Date() < new Date(inDeletion?.access.revoke_at)) {
      setDeletion(true)
    }
  }
  const isContractFlow = useIsContractFlows()
  const isPayrollOnboardingFlow = useIsPayrollOnboardingFlow()
  const hideHelpWidget = isContractFlow || isPayrollOnboardingFlow
  const showUnpaidBanner =
    !inFreeTrial &&
    !hasFeature('hide_payment_due_banner') &&
    dueBannerState &&
    ['non_blocking', 'blocking'].includes(dueBannerState.status)

  const showTrialBanner = canManageSubscription && inFreeTrial && !isContractFlow
  const showOmniButton =
    hasRegularAccess && !inFreeTrial && !inExpiredFreeTrial && !hideHelpWidget && !isEmbedded
  const showHelpButton = canManageSubscription && inFreeTrial && !hideHelpWidget
  const showBecomeBanner = isBecome
  const showDeletionBanner = isDeletion && !inFreeTrial
  const showOverlimitBanner = isOverlimit
  const showProfessionalsBanner = !loadingBookkeepers && isBookkeeper

  const showBookAMeetingBanner = trialFeaturesData?.features?.featuresConnection?.nodes?.find(
    (feature: { name: string; enabled: boolean }) => feature.name === 'show_book_a_meeting_banner'
  )?.enabled

  const showBigSegmentDemoBanner = trialFeaturesData?.features?.featuresConnection?.nodes?.find(
    (feature: { name: string; enabled: boolean }) => feature.name === 'show_big_segment_demo_banner'
  )?.enabled

  // Note: OmniButton and HelpButton are fixed-position floating buttons that don't affect layout height
  const hasBanner = Boolean(
    showTrialBanner ||
      showBecomeBanner ||
      showDeletionBanner ||
      showOverlimitBanner ||
      showProfessionalsBanner ||
      showUnpaidBanner ||
      showBookAMeetingBanner
  )

  const banners = (
    <>
      {showTrialBanner && <TrialBanner />}
      {showHelpButton && <HelpButton />}
      {showBecomeBanner && <BecomeBanner />}
      {showDeletionBanner && <DeletionBanner />}
      {showOverlimitBanner && <OverlimitEmployeesBanner />}
      {showProfessionalsBanner && <ProfessionalsBanner />}
      {showBookAMeetingBanner && (
        <BookAMeetingBanner variant={showBigSegmentDemoBanner ? 'big' : 'small'} />
      )}
      {showUnpaidBanner && (
        <UnpaidBanner
          state={dueBannerState}
          hasClockInSection={
            sidebarData?.apiCore?.moduleRegistriesConnection?.nodes.some(
              (node) => node.id === 'employee_attendance'
            ) ?? false
          }
        />
      )}
    </>
  )

  return (
    <BannerProvider hasBanner={hasBanner}>
      {canManageSubscription && inExpiredFreeTrial && <TrialExpiredModal isBecome={isBecome} />}
      {company?.deletion && <CompanyDeletionModal deletion={company.deletion} />}
      <NewNotifications />
      <InAppCommsProvider>
        <CommandBarContextProvider setOpen={setCommandBarOpen}>
          <CommandBar
            sidebarLoading={sidebarLoading}
            menuItems={commandBarMenuItems}
            isOpen={isCommandBarOpen}
            setOpen={setCommandBarOpen}
          />
          {sidebarData && (
            <FactorialOneLayout
              data={sidebarData}
              banner={banners}
              sidebar={{
                favorites: sidebarFavoritesData?.layout.favouritesConnection.nodes,
                menuCategories,
                loading: sidebarLoading || loadingFavoritesSidebar,
              }}
            >
              <AnimatedFlash />
              {!inFreeTrial && <NpsWidget currentPath={pathname} />}
              {children}
              <CurrentProgress />
              {showOmniButton && <OmniButton />}
            </FactorialOneLayout>
          )}
        </CommandBarContextProvider>
      </InAppCommsProvider>
    </BannerProvider>
  )
}

export default observer(RootLayout)
